/**
 * ticketTaxBreakdown.ts — Desglose fiscal del carrito para tickets.
 *
 * QUÉ HACE:
 * - Calcula base gravable e impuesto por línea (`tieneIVA` / `porcentajeIVA`).
 * - Soporta IVA, Impoconsumo u otra tarifa configurada por producto.
 * - Usa el NETO FINAL de cada línea (tras descuentos de ítem y su parte del
 *   descuento global): la base gravable siempre es posterior a TODOS los
 *   descuentos, de modo que base + IVA cuadran con el total cobrado en el
 *   ticket y en la exportación DIAN.
 * - Asume PVP con IVA incluido (norma comercial CO).
 *
 * QUÉ EXPORTA: computeCartTaxBreakdown, CartTaxBreakdown
 *
 * QUÉ NO DEBE HACER:
 * - No renderiza UI ni persiste datos.
 */
import type { CartItem } from "@/types";
import { taxKindLabel } from "@/lib/constants/taxRates";
import { getCartLineId } from "./cartDiscountUtils";
import type { DiscountBreakdown } from "./discountHelper";

export interface TaxRateBucket {
  ratePct: number;
  baseGravable: number;
  iva: number;
}

export interface CartTaxBreakdown {
  baseGravable: number;
  totalIva: number;
  subtotalPvp: number;
  byRate: TaxRateBucket[];
  hasIvaLines: boolean;
}

function lineNetPvp(
  item: CartItem,
  index: number,
  discountBreakdown?: DiscountBreakdown,
): number {
  const lineId = getCartLineId(index, item);
  const fromBreakdown = discountBreakdown?.lines.find(
    (l) => l.lineId === lineId,
  );
  if (fromBreakdown)
    return Math.max(
      0,
      Math.round(fromBreakdown.netoItem ?? fromBreakdown.netoAntesGlobal),
    );

  const qty = item.peso && item.peso > 0 ? 1 : Math.max(1, item.quantity || 1);
  const unit = Math.round(item.precio);
  if (item.peso && item.peso > 0) {
    return Math.round(unit * (item.peso || 0));
  }
  return Math.round(unit * qty);
}

/**
 * Tarifa efectiva de impuesto de una línea del carrito:
 *   1. Tarifa explícita del producto (tieneIVA + porcentajeIVA > 0).
 *   2. Si no la tiene, la tarifa por defecto del negocio para ítems sin tarifa
 *      (config de Caja; ej. 19% cuando todo lo vendido está gravado).
 * Devuelve 0 cuando no aplica impuesto. El PVP incluye el impuesto: esto solo
 * define el desglose base gravable/IVA, nunca cambia el precio.
 */
export function resolveLineTaxRatePct(
  item: CartItem,
  defaultRatePct = 0,
): number {
  if (item.tieneIVA && (item.porcentajeIVA ?? 0) > 0) {
    return Math.round(item.porcentajeIVA ?? 0);
  }
  return Math.max(0, Math.round(defaultRatePct ?? 0));
}

export function computeCartTaxBreakdown(
  cart: CartItem[],
  discountBreakdown?: DiscountBreakdown,
  defaultRatePct = 0,
): CartTaxBreakdown {
  const rateMap = new Map<number, { base: number; iva: number }>();
  let baseGravable = 0;
  let totalIva = 0;
  let subtotalPvp = 0;

  cart.forEach((item, index) => {
    const pvp = lineNetPvp(item, index, discountBreakdown);
    subtotalPvp += pvp;

    const rate = resolveLineTaxRatePct(item, defaultRatePct);
    if (rate > 0) {
      const base = Math.round(pvp / (1 + rate / 100));
      const iva = pvp - base;
      baseGravable += base;
      totalIva += iva;

      const bucket = rateMap.get(rate) ?? { base: 0, iva: 0 };
      bucket.base += base;
      bucket.iva += iva;
      rateMap.set(rate, bucket);
    } else {
      baseGravable += pvp;
    }
  });

  const byRate = [...rateMap.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([ratePct, { base, iva }]) => ({
      ratePct,
      baseGravable: base,
      iva,
    }));

  return {
    baseGravable,
    totalIva,
    subtotalPvp,
    byRate,
    hasIvaLines: byRate.some((b) => b.iva > 0),
  };
}

export interface IncludedTaxSummaryLine {
  ratePct: number;
  amount: number;
  itemHint: string;
}

function shortItemHintName(nombre: string): string {
  const trimmed = nombre.trim();
  const firstWord = trimmed.split(/\s+/)[0] ?? trimmed;
  if (firstWord.length <= 14) return firstWord;
  return `${firstWord.slice(0, 13)}.`;
}

function joinItemHints(names: string[]): string {
  const unique = [...new Set(names.map(shortItemHintName).filter(Boolean))];
  if (unique.length === 0) return "";
  if (unique.length === 1) return unique[0];
  if (unique.length === 2) return `${unique[0]} y ${unique[1]}`;
  const last = unique[unique.length - 1];
  return `${unique.slice(0, -1).join(", ")} y ${last}`;
}

/** Resumen informativo de impuestos incluidos (después del total a pagar). */
export function buildIncludedTaxSummaryLines(
  cart: CartItem[],
  discountBreakdown?: DiscountBreakdown,
): IncludedTaxSummaryLine[] {
  const byRate = new Map<number, { iva: number; names: string[] }>();

  cart.forEach((item, index) => {
    if (!item.tieneIVA || (item.porcentajeIVA ?? 0) <= 0) return;
    const pvp = lineNetPvp(item, index, discountBreakdown);
    const rate = Math.round(item.porcentajeIVA ?? 0);
    const base = Math.round(pvp / (1 + rate / 100));
    const iva = pvp - base;
    const bucket = byRate.get(rate) ?? { iva: 0, names: [] };
    bucket.iva += iva;
    bucket.names.push(item.nombre);
    byRate.set(rate, bucket);
  });

  return [...byRate.entries()]
    .sort((a, b) => b[0] - a[0])
    .filter(([, bucket]) => bucket.iva > 0)
    .map(([ratePct, bucket]) => ({
      ratePct,
      amount: Math.round(bucket.iva),
      itemHint: joinItemHints(bucket.names),
    }));
}

export function formatIncludedTaxSummaryLabel(
  line: IncludedTaxSummaryLine,
): string {
  const kind = taxKindLabel(line.ratePct);
  const hint = line.itemHint ? ` (${line.itemHint})` : "";
  if (line.ratePct === 0) return `Exento${hint}`;
  return `${kind} ${line.ratePct}%${hint}`;
}
